import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Table, Button, Modal, Spinner } from "react-bootstrap";
import { ArrowLeft, Trash, Eye } from "react-bootstrap-icons";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../api";
import { toast } from "react-toastify";

const StateDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get state, month, year from navigation state
  const { state, month, year } = location.state || {};
  
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalUploads, setTotalUploads] = useState(0);
  
  // Delete Modal States
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [deleting, setDeleting] = useState(false);


  // Missing Districts
const [showMissingModal, setShowMissingModal] = useState(false);
const [missingDistricts, setMissingDistricts] = useState([]);
const [missingLoading, setMissingLoading] = useState(false);

// View File Modal States
const [showViewModal, setShowViewModal] = useState(false);
const [viewLoading, setViewLoading] = useState(false);
const [viewFileName, setViewFileName] = useState("");
const [viewUrl, setViewUrl] = useState("");


  useEffect(() => {
    if (state && month && year) {
      fetchUploadDetails();
    } else {
      toast.error("Missing state information");
      navigate(-1);
    }
  }, [state, month, year]);

  const fetchUploadDetails = async () => {
    setLoading(true);
    try {
      const response = await api.post("/upload-details", {
        Month: month,
        Year: parseInt(year),
        State: state,
      });

      const data = response.data;
      setTotalUploads(data.record_count || 0);
      setUploads(data.files || []);
    } catch (error) {
      console.error("Error fetching upload details:", error);
      toast.error("Failed to fetch upload details");
      setUploads([]);
      setTotalUploads(0);
    } finally {
      setLoading(false);
    }
  };


  const fetchMissingDistricts = async () => {
  setMissingLoading(true);
  try {
    const response = await api.get("/getmissingDistrict", {
      params: {
        Month: month,
        Year: year,
        State: state,
      },
    });

    setMissingDistricts(response.data.missing_districts || []);
    setShowMissingModal(true);
  } catch (error) {
    console.error("Error fetching missing districts:", error);
    toast.error("Failed to fetch missing districts");
    setMissingDistricts([]);
  } finally {
    setMissingLoading(false);
  }
};


  const handleViewClick = async (file) => {
    setViewFileName(file.file_name);
    setViewUrl("");
    setViewLoading(true);
    setShowViewModal(true);

    try {
      const response = await api.get(`/file-preview/${file.master_id}`);
      setViewUrl(response.data.url);
    } catch (error) {
      console.error("Error loading file preview:", error);
      toast.error("Failed to load file preview");
      setShowViewModal(false);
    } finally {
      setViewLoading(false);
    }
  };

  const handleDeleteClick = (file) => {
    setSelectedFile(file);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedFile) return;

    setDeleting(true);
    try {
      await api.delete(`/master/${selectedFile.master_id}`, {
        data: { id: selectedFile.master_id },
      });

      toast.success("File deleted successfully");
      setShowDeleteModal(false);
      setSelectedFile(null);
      
      // Refresh the list
      fetchUploadDetails();
    } catch (error) {
      console.error("Error deleting file:", error);
      toast.error("Failed to delete file");
    } finally {
      setDeleting(false);
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Container fluid className="p-4 bg-theme-light" style={{ backgroundColor: "#f9fafb", minHeight: "100vh" }}>
      {/* Back Button */}
      <div className="mb-3">
        <button
          className="back-nav  fw-semibold d-flex align-items-center p-0 back-nav"
           onClick={() => navigate(-1)}
        >
          <ArrowLeft className="me-2" size={18} />
          Back to Dashboard
        </button>
      </div>

      {/* State Info Card */}
      <Card className="p-4 shadow-sm mb-4 border-0" style={{ borderRadius: "12px" }}>
        <Row className="align-items-center">
          <Col md={6}>
            <h4 className="fw-bold mb-1">{state}</h4>
            <p className="text-muted mb-0">
              Reporting Period: {month} {year}
            </p>
          </Col>
          <Col md={6} className="text-md-end mt-3 mt-md-0">
  <Button
    variant="outline-primary"
    className="mb-2"
    onClick={fetchMissingDistricts}
  >
    View Missing Districts
  </Button>

  <p className="text-muted mb-1 fw-semibold">Total Uploads</p>
  <h4 className="text-primary fw-bold mb-0">{totalUploads}</h4>
</Col>

        </Row>
      </Card>

      {/* Uploads Table */}
      <Card className="shadow-sm border-0" style={{ borderRadius: "12px" }}>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Loading uploads...</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "5%" }}>S.NO</th>
                  <th style={{ width: "40%" }}>FILE NAME</th>
                  <th style={{ width: "20%" }}>UPLOADED BY</th>
                  <th style={{ width: "25%" }}>DATE & TIME</th>
                  <th style={{ width: "5%" }} className="text-center">VIEW</th>
                  <th style={{ width: "10%" }} className="text-center">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {uploads.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-5 text-secondary">
                      <i className="bi bi-inbox display-4 d-block mb-3"></i>
                      No uploads found for this state
                    </td>
                  </tr>
                ) : (
                  uploads.map((file, index) => (
                    <tr key={file.master_id}>
                      <td className="text-muted">{index + 1}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <i className="bi bi-file-earmark-excel text-success me-2 fs-5"></i>
                          <span className="text-truncate" style={{ maxWidth: "400px" }}>
                            {file.file_name}
                          </span>
                        </div>
                      </td>
                      <td>
                          {file.user}
                      </td>
                      <td className="text-muted small">
                        {formatDateTime(file.updated_at)}
                      </td>
                      <td className="text-center">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleViewClick(file)}
                          title="View file"
                        >
                          <Eye size={16} />
                        </Button>
                      </td>
                      <td className="text-center">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteClick(file)}
                          title="Delete file"
                        >
                          <Trash size={16} />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Missing Districts Modal */}
<Modal
  show={showMissingModal}
  onHide={() => setShowMissingModal(false)}
  size="lg"
  centered
>
  <Modal.Header closeButton>
    <Modal.Title>
      Missing Districts – {state} ({month} {year})
    </Modal.Title>
  </Modal.Header>

  <Modal.Body>
    {missingLoading ? (
      <div className="text-center py-4">
        <Spinner animation="border" />
      </div>
    ) : missingDistricts.length === 0 ? (
      <p className="text-muted text-center mb-0">
        No missing districts for this period.
      </p>
    ) : (
      <Row>
        {missingDistricts.map((district, index) => (
          <Col md={4} key={index} className="mb-2">
            <div className="border rounded px-3 py-2 bg-light fw-semibold">
              {district}
            </div>
          </Col>
        ))}
      </Row>
    )}
  </Modal.Body>

  <Modal.Footer>
    <Button variant="secondary" onClick={() => setShowMissingModal(false)}>
      Close
    </Button>
  </Modal.Footer>
</Modal>


      {/* View File Modal */}
      <Modal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        fullscreen
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-truncate" style={{ maxWidth: "90%" }}>
            <i className="bi bi-file-earmark-excel text-success me-2"></i>
            {viewFileName}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-0">
          {viewLoading ? (
            <div className="d-flex flex-column align-items-center justify-content-center h-100">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Loading file...</p>
            </div>
          ) : viewUrl ? (
            <iframe
              title="xlsx-preview"
              src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                viewUrl
              )}`}
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          ) : (
            <p className="text-muted text-center py-5 mb-0">
              No file to display.
            </p>
          )}
        </Modal.Body>

        <Modal.Footer>
          <a
            href={viewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline-primary"
          >
            download
          </a>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center py-3">
            <div className="mb-3">
              <Trash size={48} className="text-danger" />
            </div>
            <h5 className="mb-3">Are you sure you want to delete this file?</h5>
            {selectedFile && (
              <p className="text-muted mb-0">
                <strong>{selectedFile.file_name}</strong>
              </p>
            )}
            <p className="text-danger small mt-2">This action cannot be undone.</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteConfirm}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Deleting...
              </>
            ) : (
              <>
                <Trash size={16} className="me-2" />
                Delete
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>

  );
};

export default StateDetails;